import React, { useMemo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withSitecoreContext } from "@sitecore-jss/sitecore-jss-react";
import { selectUserState } from "../../selectors/application";
import { pdpDeliverySelector, pdpTouchPointSelector, pdpOutOfStockSelector, pdpOrderPreviewSelector, pdpRiskCheckSelector, pdpBundleSelector, pdpBillingSelector, pdpSummarySelector, pdpPlanTypeSelector, pdpLccIdSelector } from "../../selectors/pdp";
import {
  setPdpBillingAction,
  setPdpBundleAction,
  setPdpDeliveryAction,
  setPdpLccAction,
  setPdpLoanOrderStatusAction,
  setPdpMarketAction,
  setPdpPersonalInfoAction,
  setPdpPlanTypeAction,
  setPdpRiskCheckAction,
  setPdpShippingAction,
  setPdpSummaryAction,
  setPdpTouchPointAction
} from "../../redux/actions";
import { useHistory } from "react-router-dom";
import useAddressFunctions from "../../hooks/useAddressFunctions";

import Heading from "../../elements/heading";
import BodyText from "../../elements/body-text";

import PdpDeliveryOptionRadioButtonGroup from "../../sections/pdp-delivery-option-radioButtonGroup";
import PrimaryButton from "../../elements/primary-button";
import PdpCheckoutTemplate from "../../sections/pdp-checkout-template";

import { checkoutProgressAction, setProgressBarDataAction } from "../../redux/actions/actions";
import { formatProgressBarSteps } from "../../utils/progressBar";
import { getElementOffsetY, isBizNull } from "../../utils";
import { appendQuery } from "../../utils/urlHelper";
import { getCountryByCode, getMarketCountry } from "../../services/siteService";
import useQuery, { useQueryParams } from "../../utils/useQuery";
import "./style.scss";
import { getPdpPlanType, isEmptyObject, redirect } from "../../utils/siteUtils";
import { orderTypes, PdpSpuType, PdpTowBarType, planType, siteErrorCodes, touchPointType } from "../../utils/lynkco-constants";
import { getOrderDetail } from "../../services/orderService";
import { getUserIdentity } from "../../services/userService";
import api from "../../utils/api";
import { getPdpDeliveryMethods } from "../../services/pdpService";
import ErrorView from "../../sections/error-view";
import { useErrorView } from "../../hooks/useErrorView";
import CheckoutErrorView from "../../sections/checkout-error-view";

let errorScrollTimeout;

/**
 * @constructor PdpDeliveryOptions
 * Pdp delivery page component
 * @param {PdpDeliveryOptionsFields} fields
 * @return {JSX.Element}
 */
function PdpDeliveryOptions({ sitecoreContext, fields }) {
  const query = useQuery();
  const [lid, lno, relatedOrderNo] = useQueryParams("lid", "lno", "relatedOrderNo");
  /*Redux Start*/
  const dispatch = useDispatch();
  const user = useSelector(selectUserState);
  const orderPreview = useSelector(pdpOrderPreviewSelector);
  const pdpPlanType = useSelector(pdpPlanTypeSelector);
  const pdpLccId = useSelector(pdpLccIdSelector);
  const pdpTouchPoint = useSelector(pdpTouchPointSelector);
  const pdpOutOfStock = useSelector(pdpOutOfStockSelector);
  const pdpRiskCheck = useSelector(pdpRiskCheckSelector);
  const pdpBundle = useSelector(pdpBundleSelector);
  const pdpSummary = useSelector(pdpSummarySelector);
  const pdpBilling = useSelector(pdpBillingSelector);
  const pdpDelivery = useSelector(pdpDeliverySelector);

  const updateProgressBar = data => dispatch(checkoutProgressAction(data));
  const setProgressBarData = data => dispatch(setProgressBarDataAction(data));

  const setPdpLcc = payload => dispatch(setPdpLccAction(payload));
  const setPdpPlanType = payload => dispatch(setPdpPlanTypeAction(payload));
  const setBundle = payload => dispatch(setPdpBundleAction(payload));
  const setPdpSummary = payload => dispatch(setPdpSummaryAction(payload));
  const setPersonalInfo = payload => dispatch(setPdpPersonalInfoAction(payload));
  const setBilling = payload => dispatch(setPdpBillingAction(payload));
  const setShipping = payload => dispatch(setPdpShippingAction(payload));
  const setPdpDelivery = payload => dispatch(setPdpDeliveryAction(payload));
  const setPdpTouchPoint = payload => dispatch(setPdpTouchPointAction(payload));
  const setPdpLoanOrderStatus = payload => dispatch(setPdpLoanOrderStatusAction(payload));
  const setPdpMarket = payload => dispatch(setPdpMarketAction(payload));
  const setPdpRiskCheck = payload => dispatch(setPdpRiskCheckAction(payload));

  /*Redux End*/
  const { language } = sitecoreContext;
  const lang = language.toLowerCase();
  const deliveryUnset = useRef(false);
  const templateProps = {
    fields,
    className: "start-of-sales-checkout",
    bodyClass: "start-of-sales-body",
    deliveryUnset
  };
  const { template, headingType, heading, body, deliveryInfo, submitCTA, disclaimer, shippingInfo } = fields;
  const { paymentLink } = template?.fields || {};
  const { colorScheme, iconName } = submitCTA?.fields || {};
  const { href: paymentUrl, text: submitCTAText } = submitCTA?.fields.link.value || {};
  const { touchPointVisibleCount } = deliveryInfo.fields || {};
  const visibleTpCount = isNaN(touchPointVisibleCount.value) ? 5 : Number(touchPointVisibleCount.value);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [touchPointList, setTouchPointList] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [showMoreTp, setShowMoreTp] = useState(false);
  const [touchPointMissing, setTouchPointMissing] = useState(false);
  const history = useHistory();
  const [marketCountry, setMarketCountry] = useState(null);
  const { addressList, addressLoaded, noAddress, loadAddressList, addAddress, updateAddress, removeAddress } = useAddressFunctions(fields);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const { handleError, errorViewProps } = useErrorView();
  const shippingInfoRef = React.createRef();
  const isSubmitDisabled = useMemo(() => {
    if (lid && lno) {
      return false;
    } else {
      return !marketCountry || pdpOutOfStock || !selectedDelivery;
    }
  }, [marketCountry, pdpOutOfStock, selectedDelivery]);
  useEffect(() => {
    init();
  }, []);
  useEffect(() => {
    if (user?.userAccount) {
      loadAddressList();
    }
  }, [user]);
  const redirectToPayment = () => {
    setTimeout(() => {
      const href = paymentLink.value?.href;
      if (href) {
        const url = appendQuery(href, query);
        redirect(history, url, true);
      }
    }, 10);
  };
  const getTouchPointList = async (lccId, planType, touchPointId) => {
    if (lccId) {
      const { obj } = await api.touchPoint.list(null, {
        lccId: lccId,
        touchPointType: touchPointType[planType]
      });
      const _touchPoints = obj;
      if (_touchPoints) {
        setTouchPointList(_touchPoints);
        if (touchPointId) {
          const activeIndex = _touchPoints.findIndex(x => x.id === touchPointId);
          if (activeIndex !== -1) {
            const tp = _touchPoints[activeIndex];
            setActiveId(tp.id);
            setShowMoreTp(activeIndex > visibleTpCount - 1);
          }
        }
        if (_touchPoints.length === 1 && !lid && !lno) {
          setActiveId(obj[0].id);
          setPdpTouchPoint(obj[0]);
        }
      }
      return _touchPoints;
    }
  };
  const init = async () => {
    const _marketCountry = await getMarketCountry();
    setMarketCountry(_marketCountry);
    if (lid && lno) {
      try {
        setPdpMarket(lang);
        const detail = await getOrderDetail(lid);
        const userIdentity = await getUserIdentity();
        if (!detail) {
          await handleError(siteErrorCodes.orderNotFound);
          return;
        }
        const { orderExtraData, subType, suborders, billingInfo, shippingInfo, detailedOrderStatus, riskCheckStatus } = detail;
        const { bundleId, lccId, engine, isWithTowbar, carDeliveryMethod, mccId, touchPointId } = orderExtraData;
        const { obj: bundleDetail } = await api.product.bundle.detail({
          bundleId
        });
        const { spuList } = bundleDetail;
        const bundle = {
          delivery: spuList.find(x => x.tag === PdpSpuType.delivery)?.skuList[0]
        };
        const skuQtyVoList = suborders.reduce((prev, current) => {
          return prev.concat(
            current.orderItems.map(x => ({
              skuId: x.skuId,
              qty: x.qty
            }))
          );
        }, []);
        const apiBody = {
          bundleId: bundleId,
          orderExtraData,
          skuQtyVoList,
          subType: subType
        };
        const summary = {
          apiBody,
          SECCI: null,
          engine,
          simulationId: null,
          modelConfigCode: null,
          towbarState: isWithTowbar === "1" ? PdpTowBarType.yes : PdpTowBarType.no,
          loanTermsAndConditions: pdpSummary?.loanTermsAndConditions
        };
        await setPdpLcc({
          id: lccId
        });
        await setPdpRiskCheck(riskCheckStatus);
        const pdpPlanType = getPdpPlanType(subType);
        const country = billingInfo?.countryCode ? await getCountryByCode(billingInfo.countryCode) : await getMarketCountry();
        const billing = {
          addressId: billingInfo?.id,
          street: billingInfo?.address1,
          apartment: billingInfo?.address2,
          postalCode: billingInfo?.postCode,
          city: billingInfo?.city,
          country: country?.regionName,
          countryCode: country?.regionAbbr,
          countryId: country?.regionId + ""
        };
        const shipping = {
          addressId: shippingInfo?.id,
          street: shippingInfo?.address1,
          apartment: shippingInfo?.address2,
          postalCode: shippingInfo?.postCode,
          city: shippingInfo?.city
        };
        const personalInfo = {
          firstName: userIdentity?.firstName,
          lastName: userIdentity?.firstName,
          gender: userIdentity?.gender,
          dateOfBirth: userIdentity?.birthday,
          // idType: "IDTYPE03",
          // idNumber: idNumber || "",
          city: userIdentity?.city,
          province: userIdentity?.province,
          country: userIdentity?.country,
          phoneArea: user?.mobileNoCountryCode,
          phone: user?.mobileNo
        };
        await setPdpLoanOrderStatus(detailedOrderStatus);
        await setPdpPlanType(pdpPlanType);
        await setBundle(bundle);
        await setPersonalInfo(personalInfo);
        if (isBizNull(pdpBilling)) {
          await setBilling(billing);
        }
        await setShipping(shipping);
        await setPdpSummary(summary);
        const deliverySkuIndex = skuQtyVoList.findIndex(x => x.skuId === bundle.delivery.skuId);
        const _touchpointList = await getTouchPointList(lccId, pdpPlanType, orderExtraData.touchPointId);
        let originOptions = await getPdpDeliveryMethods();
        let _deliveryOptions = [];
        if (isBizNull(_touchpointList) || _touchpointList.length === 0) {
          originOptions = originOptions.filter(x => x.value !== "pickup location");
        }
        if (deliverySkuIndex !== -1) {
          _deliveryOptions = originOptions.filter(x => x.value === "home delivery");
        } else if (orderExtraData.mccId && orderExtraData.touchPointId) {
          _deliveryOptions = originOptions.filter(x => x.value === "pickup location");
          if (isBizNull(pdpTouchPoint)) {
            let tp = originOptions.find(x => x.value === "pickup location");
            await setPdpTouchPoint({
              id: touchPointId,
              parentId: mccId,
              label: tp.label
            });
          }
        } else {
          _deliveryOptions = originOptions;
        }
        if (!pdpDelivery && _deliveryOptions.length > 0) {
          setPdpDelivery(_deliveryOptions[0]);
          setSelectedDelivery(_deliveryOptions[0]);
        }
        setDeliveryOptions(_deliveryOptions);
        if (riskCheckStatus !== "R" && !isBizNull(carDeliveryMethod)) {
          redirectToPayment();
        }
      } catch (ex) {
        await handleError(ex.code || siteErrorCodes.orderNotFound);
        console.error(ex);
      }
    } else {
      const _touchpointList = await getTouchPointList(pdpLccId, pdpPlanType, pdpTouchPoint?.id);
      let originOptions = await getPdpDeliveryMethods();
      if (isBizNull(_touchpointList) || _touchpointList.length === 0) {
        originOptions = originOptions.filter(x => x.value !== "pickup location");
      }
      if (pdpDelivery) {
        const _selectedDelivery = originOptions.find(x => x.value === pdpDelivery.value);
        setSelectedDelivery(_selectedDelivery);
      }
      setDeliveryOptions(originOptions);
    }
  };
  const onSubmit = async evt => {
    let shippingFormData;
    let valid = true;
    let checkoutFlow = "";
    // const marketCode = getMarketCountryCode();
    if (orderPreview) {
      switch (orderPreview.subType) {
        case orderTypes.monthToMonth:
          checkoutFlow = "month-to-month";
          break;
        case orderTypes.longTerm:
          checkoutFlow = "long-term";
          break;
        case orderTypes.buyACarLoan:
          checkoutFlow = "buy-car-loan";
          break;
        case orderTypes.buyACarCash:
          checkoutFlow = "buy-car-cash";
          break;
      }
    }
    if (pdpDelivery?.value === "home delivery") {
      const shippingInfo = shippingInfoRef.current.validateForm();
      shippingFormData = shippingInfo.formData;
      valid &= shippingInfo.isValid;
      setShipping(shippingFormData);
      /** Measurement */
      let dataLayer = window.dataLayer || [];

      dataLayer.push({
        event: "checkoutOption",
        option: "home delivery",
        step: 2,
        checkoutFlow: checkoutFlow
      });
    } else if (pdpDelivery?.value === "pickup location" && isBizNull(pdpTouchPoint)) {
      valid = false;
      setTouchPointMissing(true);
    } else {
      setTouchPointMissing(false);
      /** Measurement */
      let dataLayer = window.dataLayer || [];

      dataLayer.push({
        event: "checkoutOption",
        option: "pickup location",
        step: 2,
        checkoutFlow: checkoutFlow
      });
    }

    if (valid) {
      let nextUrlNew = appendQuery(paymentUrl, query);
      redirect(history, nextUrlNew);
    } else {
      if (errorScrollTimeout) {
        clearTimeout(errorScrollTimeout);
      }
      errorScrollTimeout = setTimeout(() => {
        const errorElement = document.querySelector(".checkout-delivery .show-warning");
        if (errorElement) {
          window.scrollTo({
            top: getElementOffsetY(errorElement) - 60,
            behavior: "smooth"
          });
        }
      }, 200);
    }
  };

  const submitProps = {
    buttonText: submitCTAText,
    colorScheme,
    iconName,
    isDisabled: isSubmitDisabled,
    callback(evt) {
      onSubmit(evt);
    }
  };
  const shippingInfoProps = {
    fields: shippingInfo?.fields,
    addressList,
    addressLoaded,
    noAddress,
    loadAddressList,
    addAddress,
    updateAddress,
    removeAddress,
    visible: true,
    ref: shippingInfoRef
  };
  const deliveryInfoProps = {
    fields: deliveryInfo.fields,
    touchPointMissing,
    selectedDelivery,
    setSelectedDelivery,
    deliveryOptions,
    touchPointList,
    activeId,
    setActiveId,
    showMoreTp,
    setShowMoreTp,
    visibleTpCount,
    onChange(option) {
      setTouchPointMissing(false);
    },
    shippingInfoProps,
    market: marketCountry
  };

  useEffect(() => {
    const { currentStep, _barSteps } = formatProgressBarSteps(fields.template.fields.progressBar.fields.steps);
    if ((lid || lno) && pdpRiskCheck !== "R") {
      _barSteps[0].disabled = true;
    }
    setProgressBarData(_barSteps);
    updateProgressBar(currentStep + 1);
    return function cleanUp() {
      setProgressBarData(null);
      updateProgressBar(null);
    };
  }, [pdpRiskCheck]);

  return (
    <PdpCheckoutTemplate {...templateProps}>
      <section className="car-delivery-shipping-billing">
        <div className="start-of-sales-body">
          <div className="deliveryHead">
            <div className="heading">
              <Heading type={headingType} html={heading} typographyClass="type-h2" />
            </div>
            <BodyText html={body} className="delivery-options-body" />
          </div>
          <PdpDeliveryOptionRadioButtonGroup {...deliveryInfoProps} />
          <PrimaryButton {...submitProps} />
          <BodyText className="disclaimer" html={disclaimer.value} />
        </div>
      </section>
      {errorViewProps.error && <CheckoutErrorView {...errorViewProps} />}
    </PdpCheckoutTemplate>
  );
}
export default withSitecoreContext()(PdpDeliveryOptions);
